import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  HandCoins,
  type LucideIcon,
  Receipt,
  RotateCcw,
  Scale,
  Tags,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePageTitle } from '@/components/layout/page-title';

/**
 * The "New invoice" type picker (`/invoices/new`). Presents every FA(3) document type as an identical
 * box — icon, name, and a "when to use" blurb — grouped into sales documents and corrections.
 *
 * Standard VAT and VAT margin are enabled (→ `/invoices/new/standard` and `…/standard-margin`); the rest
 * render the same but blocked, with a "Soon" badge. Each option is keyed by a
 * `{documentKind, taxScheme}` pair rather than `DocumentKind` alone — Standard VAT and VAT margin are both
 * `DocumentKind.STANDARD`, differing only by tax scheme — so enabling a future type is "give the option a
 * `to` route", not a flow rebuild.
 */

type TypeGroup = 'sales' | 'corrections';

interface InvoiceTypeOption {
  /** Slug under `invoices.type_picker.types.*` (name + description copy). */
  id: string;
  icon: LucideIcon;
  group: TypeGroup;
  /** Composer route — present only for enabled types (v1: Standard VAT alone). */
  to?: string;
  /** A "corrects the base document" type — renders the ↺ corner badge over the base-doc icon. */
  correction?: boolean;
}

/**
 * The eight FA(3) issuing options. Corrections reuse their base document's icon plus the ↺ badge, so the
 * icon carries "corrects an advance / a settlement" without extra words. Only `standard` has a `to`.
 */
const INVOICE_TYPES: InvoiceTypeOption[] = [
  { id: 'standard', icon: FileText, group: 'sales', to: '/invoices/new/standard' },
  { id: 'standard_margin', icon: Tags, group: 'sales', to: '/invoices/new/standard-margin' },
  { id: 'advance', icon: HandCoins, group: 'sales' },
  { id: 'settlement', icon: Scale, group: 'sales' },
  { id: 'simplified', icon: Receipt, group: 'sales' },
  { id: 'correction', icon: FileText, group: 'corrections', correction: true },
  { id: 'correction_of_advance', icon: HandCoins, group: 'corrections', correction: true },
  { id: 'correction_of_settlement', icon: Scale, group: 'corrections', correction: true },
];

export function NewInvoicePage() {
  const { t } = useTranslation();
  usePageTitle(t('invoices.type_picker.title'));

  const sales = INVOICE_TYPES.filter((o) => o.group === 'sales');
  const corrections = INVOICE_TYPES.filter((o) => o.group === 'corrections');

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="max-md:hidden">
        <h1 className="text-2xl font-semibold tracking-tight">{t('invoices.type_picker.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('invoices.type_picker.subtitle')}</p>
      </div>

      <ScrollArea className="cn-vertical-scroll -mr-3 min-h-0 flex-1">
        <div className="max-w-4xl space-y-8 pr-3">
          <section className="space-y-3">
            <SectionLabel>{t('invoices.type_picker.sales')}</SectionLabel>
            <TileGrid options={sales} />
          </section>

          <section className="space-y-3">
            <SectionLabel>{t('invoices.type_picker.corrections')}</SectionLabel>
            <TileGrid options={corrections} />
          </section>
        </div>
      </ScrollArea>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <h2 className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
      {children}
    </h2>
  );
}

function TileGrid({ options }: { options: InvoiceTypeOption[] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {options.map((option) => (
        <TypeTile key={option.id} option={option} />
      ))}
    </div>
  );
}

/**
 * One document-type box. Structurally identical whether enabled or not: an enabled type is an actionable
 * `<button>` opening the composer; a not-yet-available one is a blocked, dimmed `Soon` tile.
 */
function TypeTile({ option }: { option: InvoiceTypeOption }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Icon = option.icon;
  const enabled = option.to != null;
  const name = t(`invoices.type_picker.types.${option.id}.name`);
  const description = t(`invoices.type_picker.types.${option.id}.description`);

  const body = (
    <>
      {enabled ? null : (
        <span className="absolute top-3 right-3 rounded-full border bg-muted px-2 py-0.5 text-[0.625rem] font-semibold tracking-wide text-muted-foreground uppercase">
          {t('invoices.type_picker.soon')}
        </span>
      )}
      <span className="relative flex size-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-5" />
        {option.correction ? (
          <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full border bg-card text-muted-foreground">
            <RotateCcw className="size-2.5" />
          </span>
        ) : null}
      </span>
      <span className="text-sm font-semibold tracking-tight">{name}</span>
      <span className="text-xs text-muted-foreground">{description}</span>
    </>
  );

  if (enabled) {
    return (
      <button
        type="button"
        onClick={() => {
          if (option.to) navigate(option.to);
        }}
        className="relative flex flex-col gap-2.5 rounded-xl border bg-card p-4 text-left transition-colors hover:border-foreground/20 hover:bg-accent/50 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
      >
        {body}
      </button>
    );
  }

  return (
    <div
      role="button"
      aria-disabled="true"
      tabIndex={-1}
      className="relative flex cursor-not-allowed flex-col gap-2.5 rounded-xl border bg-card p-4 opacity-70"
    >
      {body}
    </div>
  );
}
